import React from "react";
import { UiSchema } from "@rjsf/utils";
import tailwindClassnames from "../../TailwindClassnames";

const processFields = (
  schema: any,
  uiSchema: UiSchema,
  errors: any,
  propertyName: string,
  readOnly: boolean = false,
) => {
  // Process fields here
  const isRequired = schema.required && schema.required.includes(propertyName);
  const title =
    schema.properties[propertyName].title ||
    propertyName.charAt(0).toUpperCase() + propertyName.slice(1) ||
    "";

  // Initialize the property in uiSchema if it doesn't exist
  uiSchema[propertyName] = uiSchema[propertyName] || {};

  // Apply readOnly status to this field
  if (readOnly) {
    uiSchema[propertyName]["ui:readonly"] = true;
    uiSchema[propertyName]["ui:disabled"] = true;
  }

  // Handle errors
  if (errors && errors[propertyName]) {
    // Only show errors if the form is not readonly
    if (!readOnly) {
      const errorMessage = errors[propertyName];
      uiSchema[propertyName]["ui:options"] = {
        ...uiSchema[propertyName]["ui:options"],
        className:
          "border border-orange-600 focus:border-orange-600 focus:ring-orange-600",
      };
      uiSchema[propertyName]["ui:help"] = (
        <div className="bg-primary-orange-light text-primary-orange-dark w-fit px-2 py-1 rounded flex flex-row items-center">
          <p className="ml-1 mt-0.5">
            {errorMessage.charAt(0).toUpperCase() + errorMessage.slice(1)}
          </p>
        </div>
      );
    }
  } else {
    uiSchema[propertyName]["ui:help"] = null; // Ensure ui:help is cleared when there are no errors
  }

  // Handle custom widgets and default widgets
  const widgetType = uiSchema[propertyName]["ui:widget"];
  const propertyType = schema.properties[propertyName].type;

  if (widgetType) {
    uiSchema[propertyName]["ui:widget"] = widgetType;
  } else if (propertyType === "file") {
    // Add accept attribute for file input
    uiSchema[propertyName]["ui:options"] = {
      ...uiSchema[propertyName]["ui:options"],
      accept: schema.properties[propertyName].accept || "*/*",
    };
  }

  // Hide Title and description for hidden fields
  if (uiSchema[propertyName]["ui:widget"] === "hidden") {
    uiSchema[propertyName]["ui:title"] = (
      <div style={{ display: "none" }}></div>
    );
    uiSchema[propertyName]["ui:description"] = (
      <div style={{ display: "none" }}></div>
    );
  } else {
    // Handle checkboxes
    if (propertyType === "boolean") {
      // For checkboxes, we want to show the title in the label, but not as a separate label above
      uiSchema[propertyName]["ui:title"] = "";

      // Ensure we get a proper label with consistent styling
      uiSchema[propertyName]["ui:options"] = {
        ...(uiSchema[propertyName]["ui:options"] || {}),
        label: title,
        classNames: "font-normal text-sm", // Ensure consistent font styling
      };

      // Handle description styling consistently for checkboxes
      if (schema.properties[propertyName].description) {
        // Custom description handling for checkbox fields
        uiSchema[propertyName]["ui:description"] = (
          <div className="field-description checkbox-description">
            {schema.properties[propertyName].description}
          </div>
        );
      }
    } else {
      // For other field types, use a standard label with required asterisk if needed
      uiSchema[propertyName]["ui:title"] = (
        <div className="flex items-baseline">
          <span className={tailwindClassnames.textInputLabel}>{title}</span>
          {isRequired && (
            <span className="mr-1 text-off-black-light dark:text-primary-off-white">
              {" "}
              *{" "}
            </span>
          )}
        </div>
      );
    }
  }
  uiSchema[propertyName]["ui:required"] = false;
};

// Function to process conditional fields dynamically
const processConditionals = (
  schema: any,
  propertyName: string,
  formData: any,
  uiSchema: UiSchema,
  errors: any,
  readOnly: boolean = false,
) => {
  if (!schema.if) return;

  const conditionMet = Object.entries(schema.if.properties || {}).every(
    ([key, condition]) =>
      formData?.[key] === (condition as { const: any }).const,
  );

  const processProperties = (conditionalSchema: any) => {
    Object.keys(conditionalSchema?.properties || {}).forEach((property) => {
      if (
        conditionalSchema.properties[property].type === "boolean" &&
        formData?.[property] === undefined
      ) {
        formData[property] =
          conditionalSchema.properties[property].default ?? false;
      }
      processFields(conditionalSchema, uiSchema, errors, property, readOnly);
      // Error handling logic
      if (errors && errors[property]) {
        const errorMessage = errors[property];
        uiSchema[property]["ui:options"] = {
          ...uiSchema[property]["ui:options"],
          className:
            "border border-orange-600 focus:border-orange-600 focus:ring-orange-600",
        };
        uiSchema[property]["ui:help"] = (
          <div className="bg-primary-orange-light text-primary-orange-dark py-1 rounded flex flex-row items-center">
            <p className="ml-1 mt-0.5">{errorMessage}</p>
          </div>
        );
      } else {
        uiSchema[property]["ui:help"] = null;
      }
    });
  };

  if (conditionMet) {
    processProperties(schema.then);
  } else {
    processProperties(schema.else);
  }
};

// Hide field unless required logic
function hideUnlessDependencyMet(
  uiSchema: UiSchema,
  formdata: any,
  targetField: string,
) {
  const dependentField =
    uiSchema.hiddenUnlessRequired?.[targetField].dependency;
  const dependentValues = Array.isArray(
    uiSchema.hiddenUnlessRequired?.[targetField].value,
  )
    ? uiSchema.hiddenUnlessRequired?.[targetField].value
    : [uiSchema.hiddenUnlessRequired?.[targetField].value];
  const shouldShow = dependentValues.includes(formdata?.[dependentField]);
  // set target field to visible if dependency is met, otherwise hide it
  if (!shouldShow) {
    uiSchema[targetField] = {
      ...(uiSchema[targetField] || {}),
      "ui:widget": "hidden",
    };
  } else if (uiSchema[targetField]?.["ui:widget"] === "hidden") {
    delete uiSchema[targetField]["ui:widget"];
  }
}

export const generateUISchema = (
  schema: any,
  providedUiSchema: UiSchema = {},
  errors: any,
  formdata: any,
  readOnly: boolean = false,
) => {
  if (schema && schema.properties) {
    const uiSchema: UiSchema = { ...providedUiSchema };

    if (schema.title) {
      uiSchema["ui:title"] = "";
    }

    // Set global readOnly/disabled status
    if (readOnly) {
      uiSchema["ui:readonly"] = true;
      uiSchema["ui:disabled"] = true;
    }

    Object.keys(schema.properties).forEach((propertyName) => {
      if (providedUiSchema?.hiddenUnlessRequired?.[propertyName]) {
        hideUnlessDependencyMet(uiSchema, formdata, propertyName);
      }
      processFields(schema, uiSchema, errors, propertyName, readOnly);
      // Process conditional properties
      processConditionals(
        schema,
        propertyName,
        formdata,
        uiSchema,
        errors,
        readOnly,
      );
    });

    // Preserve ui:order if it exists in the providedUiSchema
    if (providedUiSchema["ui:order"]) {
      uiSchema["ui:order"] = providedUiSchema["ui:order"];
    }
    return uiSchema;
  }

  return {};
};
